// AxiosContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useStore } from 'react-redux';
import { authActions } from '../store/auth';
import semver from 'semver';
import ConsoleHelper from 'utils/ConsoleHelper';
import { is } from 'react-date-range/dist/locale';


const AxiosContext = createContext();
export const useAxios = () => useContext(AxiosContext);

export const AxiosProvider = ({ children }) => {
  const store = useStore();
  const [serverDown, setServerDown] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  let intervalId;

  const instance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
  });
  const healthCheckInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
  });
  // Setup interceptors
  instance.interceptors.request.use(function (config) {
    config.headers.Authorization = store.getState().auth.token;
    config.headers['frontend-app-version'] = process.env.REACT_APP_VERSION;

    return config;
  }, function (error) {
    return Promise.reject(error);
  });

  // Function to force reload the page after a specified delay
  const forceReload = () => {
    const delayInMillis = 5000; // 5 seconds delay
    // Store the current time in localStorage to track when the reload was triggered
    window.localStorage.setItem('force-reload-triggered-time', Date.now().toString());
    // Set a timeout to reload the page after the specified delay
    setTimeout(() => {
      window.location.reload();
    }, delayInMillis);
  }

  // Interceptor for handling responses from the health check instance
  healthCheckInstance.interceptors.response.use(function (response) {
    const currentVersion = process.env.REACT_APP_VERSION; // Current version of the app
    const liveVersion = response.headers['x-version']; // Live version from the server
    const minVersionRequired = response.headers['x-min-version']; // Minimum version required by the server

    // Check if the server version is newer than the current version
    const isServerVersionNewer = semver.gt(liveVersion, currentVersion);
    // Check if the current version meets the minimum version required by the server
    let minVersionRequiredMet = undefined;
    if (minVersionRequired) {
      minVersionRequiredMet = semver.gte(currentVersion, minVersionRequired);
    }

    // If the server version is newer and the minimum version required is not met
    if (isServerVersionNewer && !minVersionRequiredMet && minVersionRequiredMet !== undefined) {

      // Get the time when the reload was last triggered from localStorage
      const reloadTriggeredTime = window.localStorage.getItem('force-reload-triggered-time');
      const sixHoursInMillis = 1000 * 60 * 60 * 6;

      // If no reload has been triggered or if it has been more than 6 hours since the last reload
      const hasReloadedWithinSixHours = reloadTriggeredTime && (Date.now() - parseInt(reloadTriggeredTime)) < sixHoursInMillis;
      if (!hasReloadedWithinSixHours) {
        forceReload();
      }
    }

    // Only update state if the value has changed
    if (isServerVersionNewer !== updateAvailable) {
      ConsoleHelper(isServerVersionNewer, 'isServerVersionNewer');
      setUpdateAvailable(isServerVersionNewer);
    }

    return response; // Continue with the response
  }, function (error) {
    return Promise.reject(error); // Handle the error
  });

  // Check if the server is up on every request
  instance.interceptors.request.use(async config => {
    try {
      await healthCheckInstance.get('/health-check');
      setServerDown(false);
    } catch (error) {
      if (error.code === 'ECONNREFUSED' || error.message === 'Network Error') {
        setServerDown(true);
      }
    }
    return config;
  }, error => Promise.reject(error));

  // check if the server response is a 401 and if so, log the user out
  instance.interceptors.response.use(response => response, error => {
    if (error.response && error.response.data) {
      if (error.response.data.message === 'Not authenticated') {
        store.dispatch(authActions.logout());
      }

      if (error.response.data.message === 'Token expired, please log in again.') {
        store.dispatch(authActions.logout())
      }

      if (error.response.data.code === "NOT_VERIFIED") {
        store.dispatch(authActions.unverifyUser());
      }

      if (error.response.status === 403 && error.response.data.message === "Subscription expired.") {
        console.log("Subscription expired");
        store.dispatch(authActions.updateActiveSubscription(false));
      }
    }

    return Promise.reject(error);
  });


  return (
    <AxiosContext.Provider value={{ instance, serverDown, updateAvailable }}>
      {children}
    </AxiosContext.Provider>
  );
};

export default AxiosProvider;
